import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  LuBookOpenCheck, LuBuilding2, LuCircleCheck, LuClipboardList, LuFileSpreadsheet, LuGraduationCap,
  LuHourglass, LuInfo, LuTriangleAlert, LuUsers,
} from 'react-icons/lu';
import DataCardList from '../../../components/data/DataCardList';
import DataTable from '../../../components/data/DataTable';
import DataToolbar from '../../../components/data/DataToolbar';
import FilterSheet from '../../../components/data/FilterSheet';
import PageHero from '../../../components/data/PageHero';
import StatTiles from '../../../components/data/StatTiles';
import TablePagination from '../../../components/data/TablePagination';
import ToggleChip from '../../../components/data/ToggleChip';
import Button from '../../../components/ui/Button';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import useElementWidth from '../../../hooks/useElementWidth';
import useMediaQuery from '../../../hooks/useMediaQuery';
import usePagedList from '../../../hooks/usePagedList';
import useTableColumns from '../../../hooks/useTableColumns';
import useViewportFit from '../../../hooks/useViewportFit';
import useEducationReport from './useEducationReport';
import useEducationReportText from './educationReportText';
import {
  ALL_STATUSES,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  REPORT_CONFIGS,
  SCOPES,
  STATUSES,
  getFilterState,
} from './educationReportColumns';
import ReportCell, { ReportCardRow, useReportCellTitle } from './parts/ReportCell';
import ReportDetailPane from './parts/ReportDetailPane';

const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

const centerName = (center) => center['Adı'] || center.name || center.centerName || center.id;

/**
 * The SOP / training completion screen shared by Eğitim Raporları
 * (`mode="all"`: every active employee) and Eğitim Yönetimi (`mode="smo"`:
 * the employees in the centers the signed-in SMO runs, with their projects
 * and department). Tiles, a SOP / training scope, a table card that reaches
 * at most the bottom of the screen and the selected employee's pending /
 * completed list beside it. Logic: useEducationReport.
 */
const EducationReportView = ({ mode }) => {
  const isSmo = mode === 'smo';
  const { employeeId, employeeData, isLoading: profileLoading } = useUserProfile();
  const {
    isLoading, rows, filteredRows, stats, options, centers, employeeCount, scope, setScope,
    searchTerm, setSearchTerm, statusFilter, setStatusFilter, filters, handleFilter, clearFilters,
    onlyOverdue, setOnlyOverdue, selectedRow, setSelectedId, downloadExcel,
  } = useEducationReport({ mode, smoId: employeeId, profileLoading });
  const { language, locale, label, statusLabel } = useEducationReportText();
  const getCellTitle = useReportCellTitle();
  const config = REPORT_CONFIGS[mode];

  const columns = useTableColumns({
    storageKey: isSmo ? 'educationManagement.tableColumns.v1' : 'educationReports.tableColumns.v1',
    columns: config.columns,
    defaultVisible: config.defaultVisible,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const hasLoaded = rows.length > 0 || !isLoading;

  const resetKey = JSON.stringify([searchTerm, statusFilter, filters, onlyOverdue, scope]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredRows, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!selectedRow) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedRow, showFilterSheet, setSelectedId]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);
  const filterOptions = useMemo(() => ({
    role: options.role.map((value) => ({ value, label: value })),
    department: options.department.map((value) => ({ value, label: value })),
    project: options.project.map((value) => ({ value, label: value })),
  }), [options]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: config.columns[key].type ? { type: config.columns[key].type, options: filterOptions[key] } : undefined,
  })), [columns.visibleKeys, columnLabel, filterOptions, config]);

  const filterChips = config.filterKeys
    .filter((key) => getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: String(filters[key]), state: 'applied' }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm) || statusFilter !== ALL_STATUSES || onlyOverdue;

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedRow ? filteredRows.findIndex((row) => row.id === selectedRow.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredRows[selectedIndex + offset];
    if (!sibling) return;
    setSelectedId(sibling.id);
    reveal(sibling.id);
  };

  const shareOf = (count) => (stats.assigned > 0 ? Math.min(1, count / stats.assigned) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const toggleCompleted = () => setStatusFilter(statusFilter === 'completed' ? ALL_STATUSES : 'completed');
  const overdueTile = { key: 'overdue', label: label('stats.overdue'), shortLabel: label('stats.overdueShort'), value: stats.overdue, icon: LuTriangleAlert, gradient: 'tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30', onClick: () => setOnlyOverdue(!onlyOverdue), active: onlyOverdue };
  const tiles = isSmo ? [
    { key: 'centers', label: label('smo.stats.centers'), shortLabel: label('smo.stats.centersShort'), value: centers.length, icon: LuBuilding2, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
    { key: 'people', label: label('smo.stats.people'), shortLabel: label('smo.stats.peopleShort'), value: employeeCount, icon: LuUsers, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'done', label: label('smo.stats.done'), shortLabel: label('stats.doneShort'), value: stats.done, format: (value) => `${value.toLocaleString(locale)}/${stats.assigned.toLocaleString(locale)}`, icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.done), shareTitle: shareTitle(stats.done), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: toggleCompleted, active: statusFilter === 'completed' },
    overdueTile,
  ] : [
    { key: 'assigned', label: label('stats.assigned'), shortLabel: label('stats.assignedShort'), value: stats.assigned, icon: LuClipboardList, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30', onClick: clearFilters, active: !isFilterActive },
    { key: 'done', label: label('stats.done'), shortLabel: label('stats.doneShort'), value: stats.done, icon: LuCircleCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', share: shareOf(stats.done), shareTitle: shareTitle(stats.done), barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500', onClick: toggleCompleted, active: statusFilter === 'completed' },
    { key: 'pending', label: label('stats.pending'), shortLabel: label('stats.pendingShort'), value: stats.pending, icon: LuHourglass, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', share: shareOf(stats.pending), shareTitle: shareTitle(stats.pending), barClass: 'tw-bg-gradient-to-r tw-from-amber-300 tw-to-orange-500' },
    overdueTile,
  ];

  const quickFilters = (
    <>
      <SegmentedControl
        size="sm"
        layoutId={`education-${mode}-scope`}
        label={label('scope.label')}
        value={scope}
        onChange={setScope}
        options={SCOPES.map((value) => ({ value, label: label(`scope.${value}`) }))}
      />
      <ToggleChip pressed={onlyOverdue} onChange={setOnlyOverdue} icon={LuTriangleAlert} label={label('toolbar.overdue')} title={label('toolbar.overdueHint')} count={hasLoaded ? stats.overduePeople : undefined} tone="amber" />
    </>
  );

  const paneProps = {
    row: selectedRow,
    showWorkplace: isSmo,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredRows.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredRows.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedId(null),
  };

  const smoName = `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim();
  const noCenters = isSmo && !isLoading && centers.length === 0;
  const emptyLabel = noCenters ? label('smo.noCenters') : label('table.empty');
  const search = isSmo
    ? { placeholder: label('smo.search'), placeholderShort: label('toolbar.searchShort') }
    : { placeholder: label('toolbar.search'), placeholderShort: label('toolbar.searchShort') };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={isSmo ? LuGraduationCap : LuBookOpenCheck}
          gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30"
          title={isSmo ? label('smo.title') : label('page.title')}
          subtitle={isSmo ? label('smo.subtitle', { name: smoName || '-' }) : label('page.subtitle')}
          actionsClassName="tw-flex tw-items-center tw-gap-2 max-sm:tw-w-full"
        >
          <Button onClick={downloadExcel} title={isSmo ? label('smo.excelHint') : label('actions.excelHint')} className="max-sm:tw-w-full">
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            {label('actions.excel')}
          </Button>
        </PageHero>

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        {isSmo && centers.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-1">
            <span className="tw-mr-1 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('smo.yourCenters')}</span>
            {centers.map((center) => (
              <span key={center.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-sky-500/20 tw-bg-sky-500/[0.08] tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-sky-700 dark:tw-text-sky-300">
                <LuBuilding2 aria-hidden="true" className="tw-h-3 tw-w-3" />
                {centerName(center)}
              </span>
            ))}
          </div>
        )}

        <p className="tw-hidden tw-items-start sm:tw-flex tw-gap-2 tw-px-1 tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">
          <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />
          {isSmo ? label('smo.note') : label('note')}
        </p>

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                ...search,
                label: search.placeholder,
                minLength: 1,
              }}
              status={{
                options: [{ value: ALL_STATUSES, label: label('toolbar.all') }, ...STATUSES.map((value) => ({ value, label: statusLabel(value) }))],
                value: statusFilter,
                onChange: setStatusFilter,
                layoutId: `education-${mode}-status`,
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: config.keys.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
              extra={quickFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(row) => <ReportCardRow row={row} showProjects={isSmo} />}
                selectedId={selectedRow?.id}
                onSelect={(row) => setSelectedId(row.id)}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={emptyLabel}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(row, key) => <ReportCell columnKey={key} row={row} />}
                getCellTitle={getCellTitle}
                selectedId={selectedRow?.id}
                onSelect={(row) => setSelectedId(row.id)}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={emptyLabel}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredRows.length}
              rangeOf={label('table.rangeOf', { total: filteredRows.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: rows.length.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <ReportDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <ReportDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={config.filterKeys.map((key) => ({ key, label: columnLabel(key), type: config.columns[key].type, options: filterOptions[key] }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredRows.length}
      />
    </>
  );
};

EducationReportView.propTypes = { mode: PropTypes.oneOf(['all', 'smo']).isRequired };

export default EducationReportView;
